import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { ExternalLink, Loader2, Link2, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CanvaHandoff — Canva Connect Design Import for ALL Lumera products.
 * Generates PPTX from the customer's actual product data and imports via Canva API.
 */
const CanvaHandoff = ({ productSlug, productId, themeId = 'modern-minimal' }) => {
  const [canvaConnected, setCanvaConnected] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importPhase, setImportPhase] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/canva-connect/status`);
        setCanvaConnected(res.data.connected);
      } catch {
        setCanvaConnected(false);
      }
    };
    check();

    const params = new URLSearchParams(window.location.search);
    if (params.get('canva_connected') === 'true') {
      setCanvaConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('canva_error')) {
      setError('Canva connection failed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleConnect = async () => {
    try {
      const redirectPath = window.location.pathname + '?canva_connected=true';
      const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const res = await axios.get(`${API}/canva-connect/authorize`, {
        params: { redirect_after: redirectPath, base_url: baseUrl },
      });
      window.location.href = res.data.auth_url;
    } catch {
      setError('Failed to start Canva connection.');
    }
  };

  const handleImport = useCallback(async () => {
    setImporting(true);
    setImportPhase('Generating your design...');
    setError(null);
    setSuccess(null);

    try {
      const res = await axios.post(`${API}/canva-connect/import-design`, {
        product_id: productId || '',
        product_type: productSlug || '',
        theme_id: themeId || 'modern-minimal',
      });

      const jobId = res.data.job_id;
      if (!jobId) throw new Error('No job ID returned');

      setImportPhase('Importing to Canva...');

      let attempts = 0;
      const maxAttempts = 30;

      const poll = async () => {
        attempts++;
        try {
          const statusRes = await axios.get(`${API}/canva-connect/import-status/${jobId}`);
          const { status, edit_url, error: jobError } = statusRes.data;

          if (status === 'success' && edit_url) {
            setImporting(false);
            setSuccess({ edit_url });
            setImportPhase('');
            if (pollRef.current) clearInterval(pollRef.current);
            window.open(edit_url, '_blank', 'noopener,noreferrer');
            return;
          }
          if (status === 'failed') {
            setImporting(false);
            setError(jobError || 'Canva import failed.');
            setImportPhase('');
            if (pollRef.current) clearInterval(pollRef.current);
            return;
          }
          if (attempts >= maxAttempts) {
            setImporting(false);
            setError('Import timed out. Please try again.');
            setImportPhase('');
            if (pollRef.current) clearInterval(pollRef.current);
          }
        } catch {
          if (attempts >= maxAttempts) {
            setImporting(false);
            setError('Failed to check import status.');
            setImportPhase('');
            if (pollRef.current) clearInterval(pollRef.current);
          }
        }
      };

      pollRef.current = setInterval(poll, 2000);
      poll();
    } catch (err) {
      setImporting(false);
      const detail = err.response?.data?.detail || '';
      if (detail === 'canva_not_connected' || detail === 'canva_token_expired') {
        setCanvaConnected(false);
        setError('Canva session expired. Please reconnect.');
      } else {
        setError(detail || 'Import failed. Please try again.');
      }
    }
  }, [productId, productSlug, themeId]);

  const handleDownloadPptx = async () => {
    try {
      const pid = productId || 'latest';
      const params = new URLSearchParams();
      if (themeId) params.set('theme_id', themeId);
      if (productSlug) params.set('product_type', productSlug);
      const res = await axios.get(`${API}/canva-connect/download-pptx/${pid}?${params}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Lumera-Design-for-Canva.pptx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Download failed.');
    }
  };

  if (canvaConnected === null) {
    return (
      <div className="border-t border-stone-100 mt-4 pt-4">
        <div className="flex items-center justify-center py-3">
          <Loader2 className="w-4 h-4 animate-spin text-stone-300" />
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-stone-100 mt-4 pt-4" data-testid="canva-connect-handoff">
      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg p-2.5 mb-3" data-testid="canva-error">
          <AlertCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] font-sans text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-100 rounded-lg p-2.5 mb-3" data-testid="canva-success">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[11px] font-sans text-emerald-700 font-semibold">Design imported to Canva!</p>
            <a href={success.edit_url} target="_blank" rel="noopener noreferrer"
              className="text-[10px] font-sans text-emerald-600 hover:underline inline-flex items-center gap-1 mt-0.5">
              Open in Canva <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}

      {!canvaConnected && !importing && (
        <>
          <Button data-testid="canva-connect-btn" onClick={handleConnect} variant="outline"
            className="w-full rounded-xl py-5 font-sans font-semibold border-[#00C4CC]/30 text-[#00C4CC] hover:bg-[#00C4CC]/5 hover:border-[#00C4CC]/50 transition-all">
            <Link2 className="w-4 h-4 mr-2" />
            Connect Canva Account
          </Button>
          <p className="text-[10px] font-sans text-stone-400 mt-2 text-center leading-relaxed">
            Connect your free Canva account to import your design for further editing.
          </p>
        </>
      )}

      {canvaConnected && !importing && (
        <>
          <Button data-testid="canva-import-btn" onClick={handleImport} variant="outline"
            className="w-full rounded-xl py-5 font-sans font-semibold border-[#00C4CC]/30 text-[#00C4CC] hover:bg-[#00C4CC]/5 hover:border-[#00C4CC]/50 transition-all">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-9l6 4.5-6 4.5z"/>
            </svg>
            Continue in Canva
            <ExternalLink className="w-3.5 h-3.5 ml-1.5 opacity-60" />
          </Button>
          <p className="text-[10px] font-sans text-stone-400 mt-2 text-center leading-relaxed">
            Import your personalized design into Canva to customize further. Works with Canva Free.
          </p>
          <button data-testid="canva-download-pptx" onClick={handleDownloadPptx}
            className="w-full text-center mt-2 text-[10px] font-sans text-stone-400 hover:text-stone-600 transition-colors">
            Or download .pptx for manual import
          </button>
        </>
      )}

      {importing && (
        <div className="text-center py-3" data-testid="canva-importing">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-[#00C4CC]" />
          <p className="text-xs font-sans font-medium text-stone-600">{importPhase}</p>
          <p className="text-[10px] font-sans text-stone-400 mt-1">This usually takes 5-15 seconds...</p>
        </div>
      )}
    </div>
  );
};

export default CanvaHandoff;
